// Helpers for multi-branch cells. A branch cell lists one or more branches in a
// single comma-delimited string (mirroring the multistate states cell) — one
// row's rules applying to every listed branch.
//
// The wrinkle: a comma is BOTH our delimiter and a legal character in a branch
// name ("Boston, MA"). We disambiguate by escaping literal commas as "\," when
// JOINING a multi-branch cell, and splitting only on UN-escaped commas when
// PARSING. A single-branch cell is never escaped (so the common case stays
// clean), and parsing tries a whole-cell exact match against known names first
// — that covers a lone comma-named branch and CSV input, where authors type the
// raw name without escapes.

const ESCAPED_COMMA = '\\,'

// Escape literal commas in a branch name so they survive a comma-delimited join.
const escapeBranchName = (name) => String(name).replace(/,/g, ESCAPED_COMMA)

// Split a cell on UN-escaped commas, then unescape each segment. Hand-written
// (no regex lookbehind) for broad browser support.
const splitEscapedBranches = (cell) => {
  const out = []
  let current = ''
  for (let i = 0; i < cell.length; i++) {
    const ch = cell[i]
    if (ch === '\\' && cell[i + 1] === ',') {
      current += ','
      i++ // consume the escaped comma
    } else if (ch === ',') {
      out.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  out.push(current.trim())
  return out.filter(Boolean)
}

// Serialize selected branch names into a cell value. A single branch is left
// raw (clean display, resolved by whole-cell match on parse); multiple branches
// are escaped + comma-joined so the delimiter is unambiguous.
export const joinBranchNames = (names) => {
  const clean = (names || []).filter((n) => n != null && String(n).trim() !== '')
  if (clean.length <= 1) {
    return clean[0] != null ? String(clean[0]) : ''
  }
  return clean.map(escapeBranchName).join(', ')
}

// Parse a branch cell into branch names. Whole-cell exact match first (a lone
// comma-named branch, or CSV input typed without escapes), then split on
// un-escaped commas. So "Boston, MA" alone resolves to that one branch, while
// the escaped join "Boston\, MA, Reno" resolves to ["Boston, MA", "Reno"].
export const parseBranchCellNames = (cellValue, branches) => {
  const raw = (cellValue || '').trim()
  if (!raw) {
    return []
  }
  if ((branches || []).some((branch) => branch.name === raw)) {
    return [raw]
  }
  return splitEscapedBranches(raw)
}
